import { FC, lazy, memo, Suspense, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { FaroErrorBoundary } from "@grafana/faro-react";
import { Toaster } from "@/components/ui/sonner";
import { LocalRoutes } from "@/LocalRoutes";
import { ProtectedRoute } from "@/auth/protected-route";
import { useFaroUser } from "@/hooks/use-faro-user";
import { usePlatformUnavailableReason } from "@/hooks/use-platform-unavailable";
import {
    isChunkLoadError,
    reloadOnceForStaleChunks,
    shouldReloadForStaleChunks,
} from "@/lib/chunk-reload";
import i18next from "i18next";
import NotFound from "@/app/not-found/page";
import Unauthorized from "@/app/unauthorized/page";
import PlatformUnavailable from "@/app/platform-unavailable/page";
import Login from "@/app/login/page";
// import Registration from "@/app/registration/page";
import PasswordReset from "@/app/password-reset/page";
import Home from "@/app/home/page";
import SidebarLayout from "@/app/layout";
import { Roles } from "./auth/user-roles";

const Dashboard = lazy(() => import("@/app/dashboard/page"));
const Admin = lazy(() => import("@/app/admin/page"));
const AdminDashboard = lazy(() => import("@/app/admin/dashboard/page"));
const EdaProcesses = lazy(() => import("@/app/debug/eda-conversations/page"));
const EdaMessages = lazy(() => import("@/app/debug/eda-messages/page"));
const Users = lazy(() => import("@/app/admin/users/page"));
const EnergyCommunities = lazy(
    () => import("@/app/admin/energy-communities/page"),
);
const EnergyCommunityBilling = lazy(
    () => import("@/app/admin/energy-communities/[id]/billing/page"),
);
const InvoiceControlling = lazy(
    () => import("@/app/admin/invoice-controlling/page"),
);
const EnergyCommunityMemberContracts = lazy(
    () => import("@/app/admin/energy-community-member-contracts/page"),
);
const MemberContractBilling = lazy(
    () =>
        import("@/app/admin/energy-community-member-contracts/[id]/billing/page"),
);
const MeteringPoints = lazy(() => import("@/app/admin/metering-points/page"));
const MeteringPointContracts = lazy(
    () => import("@/app/admin/metering-point-contracts/page"),
);
const ProcessHistory = lazy(
    () => import("@/app/admin/metering-point-contracts/[id]/processes/page"),
);
const PaymentInfos = lazy(() => import("@/app/admin/payment-infos/page"));
const SepaMandates = lazy(() => import("@/app/admin/sepa-mandates/page"));
const AdminDocuments = lazy(() => import("@/app/admin/documents/page"));
const LegalPersons = lazy(() => import("@/app/admin/legal-persons/page"));
const GridOperators = lazy(() => import("@/app/admin/grid-operators/page"));
const SystemBanner = lazy(() => import("@/app/admin/system-banner/page"));
const TariffComponents = lazy(
    () => import("@/app/admin/tariff-components/page"),
);
const EditTariffComponent = lazy(
    () => import("@/app/admin/tariff-components/[id]/page"),
);
const TariffPackages = lazy(() => import("@/app/admin/tariff-packages/page"));
const EditTariffPackage = lazy(
    () => import("@/app/admin/tariff-packages/[id]/page"),
);
const TariffAssignments = lazy(
    () => import("@/app/admin/tariff-assignments/page"),
);
const TariffView = lazy(() => import("@/app/admin/tariff-view/page"));
const TariffViewMPC = lazy(
    () => import("@/app/admin/tariff-view/[mpcId]/page"),
);
const EnergyDataInfo = lazy(() => import("@/app/energy-data/info/page"));
const Billing = lazy(() => import("@/app/billing/page"));
const UserMeteringPoints = lazy(() => import("@/app/metering-points/page"));
const DataExports = lazy(() => import("@/app/data-exports/page"));
const Notifications = lazy(() => import("@/app/notifications/page"));
const AdminNotifications = lazy(() => import("@/app/admin/notifications/page"));
const OnboardingControlling = lazy(
    () => import("@/app/admin/onboarding-controlling/page"),
);
const MessageInterventions = lazy(
    () => import("@/app/admin/message-interventions/page"),
);
const Invoices = lazy(() => import("@/app/admin/invoices/page"));
const UserDataPage = lazy(() => import("@/app/user-data/page"));
const UserLegalPersons = lazy(() => import("@/app/legal-persons/page"));
const MemberContracts = lazy(() => import("@/app/member-contracts/page"));
const ContractDocuments = lazy(() => import("@/app/contract-documents/page"));
const UserMeteringPointContracts = lazy(
    () => import("@/app/metering-point-contracts/page"),
);
const InvoicesPage = lazy(() => import("@/app/invoices/page"));
const BillingBatchLog = lazy(
    () => import("@/app/admin/billing-batch-log/page"),
);
const BankTransactions = lazy(
    () => import("@/app/admin/bank-transactions/page"),
);

const RouteFallback: FC = () => (
    <div className="flex h-full w-full items-center justify-center p-8">
        <Loader2 className="animate-spin text-muted-foreground" size={32} />
    </div>
);

const AppRoutes: FC = () => (
    <Suspense fallback={<RouteFallback />}>
        <Routes>
            <Route element={<Home />} path={LocalRoutes.HOME} />
            <Route element={<Login />} path={LocalRoutes.LOGIN} />
            {/* <Route path={LocalRoutes.REGISTRATION} element={<Registration />} /> */}
            <Route
                element={<PasswordReset />}
                path={LocalRoutes.PASSWORD_RESET}
            />
            <Route element={<SidebarLayout />}>
                <Route
                    element={<ProtectedRoute requiredRoles={[Roles.USER]} />}
                >
                    <Route
                        element={<Dashboard />}
                        path={LocalRoutes.DASHBOARD}
                    />
                    <Route
                        element={<UserMeteringPoints />}
                        path={LocalRoutes.METERING_POINTS}
                    />
                    <Route element={<Billing />} path={LocalRoutes.BILLING} />
                    <Route
                        element={<UserDataPage />}
                        path={LocalRoutes.USER_DATA}
                    />
                    <Route
                        element={<UserLegalPersons />}
                        path={LocalRoutes.LEGAL_PERSONS}
                    />
                    <Route
                        element={<Notifications />}
                        path={LocalRoutes.NOTIFICATIONS}
                    />
                    <Route
                        element={<MemberContracts />}
                        path={LocalRoutes.MEMBER_CONTRACTS}
                    />
                    <Route
                        element={<ContractDocuments />}
                        path={LocalRoutes.CONTRACT_DOCUMENTS}
                    />
                    <Route
                        element={<UserMeteringPointContracts />}
                        path={LocalRoutes.METERING_POINT_CONTRACTS}
                    />
                    <Route
                        element={<InvoicesPage />}
                        path={LocalRoutes.INVOICES}
                    />
                    <Route
                        element={<DataExports />}
                        path={LocalRoutes.DATA_EXPORTS}
                    />
                </Route>

                <Route
                    element={<ProtectedRoute requiredRoles={[Roles.ADMIN]} />}
                >
                    <Route
                        element={<EnergyDataInfo />}
                        path={LocalRoutes.ENERGY_DATA_INFO_FOR_CONTRACT}
                    />
                    <Route element={<Admin />} path={LocalRoutes.ADMIN} />
                    <Route
                        element={<AdminDashboard />}
                        path={LocalRoutes.ADMIN_DASHBOARD}
                    />
                    <Route
                        element={<EnergyCommunities />}
                        path={LocalRoutes.ADMIN_ENERGY_COMMUNITIES}
                    />
                    <Route
                        element={<EnergyCommunityBilling />}
                        path={LocalRoutes.ADMIN_ENERGY_COMMUNITY_BILLING}
                    />
                    <Route
                        element={<InvoiceControlling />}
                        path={LocalRoutes.ADMIN_INVOICE_CONTROLLING}
                    />
                    <Route
                        element={<EnergyCommunityMemberContracts />}
                        path={
                            LocalRoutes.ADMIN_ENERGY_COMMUNITY_MEMBER_CONTRACTS
                        }
                    />
                    <Route
                        element={<MemberContractBilling />}
                        path={LocalRoutes.ADMIN_MEMBER_CONTRACT_BILLING}
                    />
                    <Route
                        element={<MeteringPoints />}
                        path={LocalRoutes.ADMIN_METERING_POINTS}
                    />
                    <Route
                        element={<MeteringPointContracts />}
                        path={LocalRoutes.ADMIN_METERING_POINT_CONTRACTS}
                    />
                    <Route
                        element={<ProcessHistory />}
                        path={
                            LocalRoutes.ADMIN_METERING_POINT_CONTRACT_PROCESSES
                        }
                    />
                    <Route
                        element={<PaymentInfos />}
                        path={LocalRoutes.ADMIN_PAYMENT_INFOS}
                    />
                    <Route
                        element={<SepaMandates />}
                        path={LocalRoutes.ADMIN_SEPA_MANDATES}
                    />
                    <Route
                        element={<AdminDocuments />}
                        path={LocalRoutes.ADMIN_DOCUMENTS}
                    />
                    <Route
                        element={<LegalPersons />}
                        path={LocalRoutes.ADMIN_LEGAL_PERSONS}
                    />
                    <Route
                        element={<GridOperators />}
                        path={LocalRoutes.ADMIN_GRID_OPERATORS}
                    />
                    <Route
                        element={<TariffComponents />}
                        path={LocalRoutes.ADMIN_TARIFF_COMPONENTS}
                    />
                    <Route
                        element={<EditTariffComponent />}
                        path={LocalRoutes.ADMIN_TARIFF_COMPONENT_EDIT}
                    />
                    <Route
                        element={<TariffPackages />}
                        path={LocalRoutes.ADMIN_TARIFF_PACKAGES}
                    />
                    <Route
                        element={<EditTariffPackage />}
                        path={LocalRoutes.ADMIN_TARIFF_PACKAGE_EDIT}
                    />
                    <Route
                        element={<TariffAssignments />}
                        path={LocalRoutes.ADMIN_TARIFF_ASSIGNMENTS}
                    />
                    <Route
                        element={<TariffView />}
                        path={LocalRoutes.ADMIN_TARIFF_VIEW}
                    />
                    <Route
                        element={<TariffViewMPC />}
                        path={LocalRoutes.ADMIN_TARIFF_VIEW_MPC}
                    />
                    <Route
                        element={<AdminNotifications />}
                        path={LocalRoutes.ADMIN_NOTIFICATIONS}
                    />
                    <Route
                        element={<OnboardingControlling />}
                        path={LocalRoutes.ADMIN_ONBOARDING_CONTROLLING}
                    />
                    <Route
                        element={<MessageInterventions />}
                        path={LocalRoutes.ADMIN_MESSAGE_INTERVENTIONS}
                    />
                    <Route
                        element={<Invoices />}
                        path={LocalRoutes.ADMIN_INVOICES}
                    />
                    <Route
                        element={<BillingBatchLog />}
                        path={LocalRoutes.ADMIN_BILLING_BATCH_LOG}
                    />
                    <Route
                        element={<BankTransactions />}
                        path={LocalRoutes.ADMIN_BANK_TRANSACTIONS}
                    />
                    <Route element={<Users />} path={LocalRoutes.ADMIN_USERS} />
                </Route>
                <Route
                    element={<ProtectedRoute requiredRoles={[Roles.ROOT]} />}
                >
                    <Route
                        element={<SystemBanner />}
                        path={LocalRoutes.ADMIN_SYSTEM_BANNER}
                    />
                    <Route
                        element={<EdaProcesses />}
                        path={LocalRoutes.DEBUG_EDA_PROCESSES}
                    />
                    <Route
                        element={<EdaMessages />}
                        path={LocalRoutes.DEBUG_EDA_MESSAGES}
                    />
                </Route>
                <Route
                    element={
                        <ProtectedRoute
                            requiredRoles={[Roles.SUPER_ROOT_3000]}
                        />
                    }
                />
                <Route
                    element={<Unauthorized />}
                    path={LocalRoutes.UNAUTHORIZED}
                />
                <Route element={<NotFound />} path="*" />
            </Route>
        </Routes>
    </Suspense>
);

const AppContent = memo(() => <AppRoutes />);

AppContent.displayName = "AppContent";

// React catches rejections from lazy() route imports and routes them to the
// error boundary, so the global handlers in index.tsx never see them. Without
// this the user is stuck on the fallback after a deploy replaced the hashed
// chunks, even though a reload would fix it.
const ErrorFallback: FC<{ error: Error | null }> = ({ error }) => {
    const staleChunk = isChunkLoadError(error) && shouldReloadForStaleChunks();

    useEffect(() => {
        if (staleChunk) {
            reloadOnceForStaleChunks();
        }
    }, [staleChunk]);

    if (staleChunk) {
        return (
            <div className="flex h-full w-full items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full items-center justify-center p-8 text-center">
            <div>
                <h1 className="text-xl font-semibold">
                    {i18next.t("common.something-went-wrong")}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                    {error?.message ?? i18next.t("common.default-error")}
                </p>
            </div>
        </div>
    );
};

const App: FC = () => {
    useFaroUser();
    const unavailableReason = usePlatformUnavailableReason();
    return (
        <FaroErrorBoundary
            fallback={(error) => <ErrorFallback error={error} />}
        >
            {unavailableReason ? (
                <PlatformUnavailable reason={unavailableReason} />
            ) : (
                <AppContent />
            )}
            <Toaster duration={10000} position="top-right" richColors />
        </FaroErrorBoundary>
    );
};

export default App;
